import React, { useState } from "react";
import {
    IoClose,
    IoCheckmarkCircle,
    IoPerson,
    IoCalendar,
    IoTime,
    IoFlag,
    IoLocationSharp,
    IoGrid,
    IoChevronDown,
} from "react-icons/io5";

const TicketModal = ({
    ticket,
    onClose,
    onStatusChange,
    onAssignUser,
    onPriorityChange,
    updating,
    getStatusColor,
    getPriorityColor,
    getStatusIcon,
    users = [],
    isSubAdmin
}) => {
    const [showAssignMenu, setShowAssignMenu] = useState(false);
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [showPriorityMenu, setShowPriorityMenu] = useState(false);

    if (!ticket) return null;

    const openFullscreenImage = (imageUrl) => {
        setFullscreenImage(imageUrl);
    };

    const closeFullscreenImage = () => {
        setFullscreenImage(null);
    };

    const statusOptions = [
        { value: "pending", label: "Pending", color: "yellow" },
        { value: "in_progress", label: "In Progress", color: "blue" },
        { value: "resolved", label: "Resolved", color: "green" },
        { value: "rejected", label: "Rejected", color: "red" }
    ];

    const priorityOptions = [
        { value: "low", label: "Low", color: "gray" },
        { value: "medium", label: "Medium", color: "blue" },
        { value: "high", label: "High", color: "orange" },
        { value: "critical", label: "Critical", color: "red" }
    ];

    return (
        <>
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={onClose}
            >
                <div
                    className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Modal Header */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Ticket Details</h2>
                            <p className="text-xs text-gray-600 mt-1">ID: {ticket._id}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <IoClose className="w-6 h-6 text-gray-600" />
                        </button>
                    </div>

                    <div className="p-6">
                        {/* Status & Priority */}
                        <div className="flex gap-3 mb-6 flex-wrap">
                            {/* Status Dropdown */}
                            {!isSubAdmin && (<div className="relative">
                                <button
                                    onClick={() => {
                                        setShowStatusMenu(!showStatusMenu);
                                        setShowPriorityMenu(false);
                                    }}
                                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${getStatusColor(ticket.status)} hover:opacity-80 transition-opacity cursor-pointer`}
                                >
                                    {getStatusIcon(ticket.status)}
                                    {ticket.status.replace('_', ' ').toUpperCase()}
                                    <IoChevronDown className="w-3 h-3" />
                                </button>

                                {showStatusMenu && (
                                    <div className="absolute top-full left-0 mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl overflow-hidden z-20 min-w-[200px]">
                                        <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-3 py-2 border-b border-gray-200">
                                            <p className="text-xs font-semibold text-gray-700">Change Status</p>
                                        </div>
                                        {statusOptions.map(option => (
                                            <button
                                                key={option.value}
                                                onClick={() => {
                                                    onStatusChange(ticket._id, option.value);
                                                    setShowStatusMenu(false);
                                                }}
                                                disabled={updating || ticket.status === option.value}
                                                className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center gap-2 border-b border-gray-100 last:border-0 ${ticket.status === option.value ? 'bg-gray-50 cursor-not-allowed opacity-50' : ''
                                                    }`}
                                            >
                                                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(option.value)}`}>
                                                    {getStatusIcon(option.value)}
                                                    {option.label}
                                                </span>
                                                {ticket.status === option.value && (
                                                    <IoCheckmarkCircle className="w-4 h-4 text-green-600 ml-auto" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>)}

                            {!isSubAdmin && (<div className="relative">
                                <button
                                    onClick={() => {
                                        setShowPriorityMenu(!showPriorityMenu);
                                        setShowStatusMenu(false);
                                    }}
                                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${getPriorityColor(ticket.priorite)} hover:opacity-80 transition-opacity cursor-pointer`}
                                >
                                    <IoFlag className="w-4 h-4" />
                                    {ticket.priorite.toUpperCase()} PRIORITY
                                    <IoChevronDown className="w-3 h-3" />
                                </button>

                                {showPriorityMenu && (
                                    <div className="absolute top-full left-0 mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl overflow-hidden z-20 min-w-[200px]">
                                        <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-3 py-2 border-b border-gray-200">
                                            <p className="text-xs font-semibold text-gray-700">Change Priority</p>
                                        </div>
                                        {priorityOptions.map(option => (
                                            <button
                                                key={option.value}
                                                onClick={() => {
                                                    onPriorityChange(ticket._id, option.value);
                                                    setShowPriorityMenu(false);
                                                }}
                                                disabled={updating || ticket.priorite === option.value}
                                                className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center gap-2 border-b border-gray-100 last:border-0 ${ticket.priorite === option.value ? 'bg-gray-50 cursor-not-allowed opacity-50' : ''
                                                    }`}
                                            >
                                                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold border ${getPriorityColor(option.value)}`}>
                                                    <IoFlag className="w-3 h-3" />
                                                    {option.label}
                                                </span>
                                                {ticket.priorite === option.value && (
                                                    <IoCheckmarkCircle className="w-4 h-4 text-green-600 ml-auto" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>)}
                        </div>

                        <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <IoPerson className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 text-base">
                                    {ticket.createdby?.name || "Unknown user"}
                                </h3>
                                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                                    <IoCalendar className="w-4 h-4" />
                                    <span>
                                        {new Date(ticket.createdAt).toLocaleDateString("en-US", {
                                            month: "long",
                                            day: "numeric",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="p-4 bg-blue-50 rounded-xl">
                                <div className="flex items-center gap-2 mb-2">
                                    <IoGrid className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm font-semibold text-blue-900">Stand / Hall</span>
                                </div>
                                <p className="text-base font-bold text-blue-700">{ticket.stand}</p>
                            </div>
                            {ticket.location && (
                                <div className="p-4 bg-red-50 rounded-xl">
                                    <div className="flex items-center gap-2 mb-2">
                                        <IoLocationSharp className="w-5 h-5 text-red-600" />
                                        <span className="text-sm font-semibold text-red-900">Location</span>
                                    </div>
                                    <p className="text-base font-bold text-red-700">{ticket.location}</p>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div className="mb-6">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                Description
                            </h4>
                            <p className="text-gray-800 leading-relaxed text-sm bg-gray-50 p-4 rounded-xl">
                                {ticket.description}
                            </p>
                        </div>

                        {/* Assignment */}
                        <div className="mb-6">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                Assignment
                            </h4>
                            <div className="relative">
                                {ticket.assignedUser ? (
                                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-md">
                                                    <IoPerson className="w-6 h-6 text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Assigned To</p>
                                                    <p className="text-lg font-bold text-green-900">{ticket.assignedUser.name}</p>
                                                </div>
                                            </div>
                                            {!isSubAdmin && (<button
                                                onClick={() => setShowAssignMenu(!showAssignMenu)}
                                                className="px-4 py-2 text-sm bg-white border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-600 hover:text-white transition-all font-semibold shadow-sm"
                                            >
                                                Change
                                            </button>)}
                                        </div>
                                        {ticket.assignedAt && (
                                            <div className="flex items-center gap-2 text-xs text-green-700">
                                                <IoTime className="w-3.5 h-3.5" />
                                                <span>Assigned {new Date(ticket.assignedAt).toLocaleString("en-US", {
                                                    month: "short",
                                                    day: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit"
                                                })}</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    !isSubAdmin && (<button
                                        onClick={() => setShowAssignMenu(!showAssignMenu)}
                                        className="w-full p-5 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-400 hover:from-blue-50 hover:to-indigo-50 transition-all group"
                                    >
                                        <div className="flex items-center justify-center gap-3">
                                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                                                <IoPerson className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
                                            </div>
                                            <span className="font-semibold text-gray-600 group-hover:text-blue-600 transition-colors">
                                                Click to Assign User
                                            </span>
                                        </div>
                                    </button>)
                                )}

                                {/* Assignment Dropdown */}
                                {showAssignMenu && (
                                    <div className="mt-3 bg-white border-2 border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-700">Select User to Assign</p>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto">
                                            {users.length > 0 ? (
                                                users.map(user => (
                                                    <button
                                                        key={user._id}
                                                        onClick={() => {
                                                            onAssignUser(ticket._id, user._id);
                                                            setShowAssignMenu(false);
                                                        }}
                                                        className="w-full px-4 py-3.5 text-left hover:bg-blue-50 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-0 group"
                                                    >
                                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                                                            <IoPerson className="w-5 h-5 text-white" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <span className="font-semibold text-gray-900 block">{user.name}</span>
                                                            {user.role && (
                                                                <span className="text-xs text-gray-500">{user.role}</span>
                                                            )}
                                                        </div>
                                                        <IoChevronDown className="w-5 h-5 text-gray-400 -rotate-90" />
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="px-4 py-8 text-center">
                                                    <IoPerson className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                                                    <p className="text-sm text-gray-500 font-medium">No users available</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* SLA Info */}
                        {ticket.slaDeadline && (
                            <div className="mb-6 p-4 bg-orange-50 rounded-xl">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <IoTime className="w-5 h-5 text-orange-600" />
                                            <span className="text-sm font-semibold text-orange-900">SLA Deadline</span>
                                        </div>
                                        <p className="text-base font-bold text-orange-700">
                                            {new Date(ticket.slaDeadline).toLocaleString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </p>
                                    </div>
                                    {ticket.isOverdue && (
                                        <span className="px-3 py-1 bg-red-600 text-white text-sm font-bold rounded-lg">
                                            OVERDUE
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Photos */}
                        {ticket.photos && ticket.photos.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                    Attached Photos ({ticket.photos.length})
                                </h4>
                                <div className="grid grid-cols-2 gap-4">
                                    {ticket.photos.map((photo, index) => (
                                        <div
                                            key={index}
                                            className="relative rounded-xl overflow-hidden bg-gray-50 border border-gray-200 hover:border-blue-300 transition-all cursor-pointer group"
                                            onClick={() => openFullscreenImage(photo)}
                                        >
                                            <img
                                                src={photo}
                                                alt={`Evidence ${index + 1}`}
                                                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                                            />
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                                <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                                {index + 1} / {ticket.photos.length}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Messages */}
                        {ticket.messages && ticket.messages.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                    Messages ({ticket.messages.length})
                                </h4>
                                <div className="space-y-3">
                                    {ticket.messages.map((msg, index) => (
                                        <div key={index} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                                                    <IoPerson className="w-4 h-4 text-white" />
                                                </div>
                                                <span className="font-semibold text-gray-900 text-sm">{msg.user?.name || "User"}</span>
                                                <span className="text-xs text-gray-500">
                                                    {new Date(msg.createdAt).toLocaleString("en-US", {
                                                        month: "short",
                                                        day: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </span>
                                            </div>
                                            <p className="text-gray-700 text-sm">{msg.message}</p>
                                            {msg.images && msg.images.length > 0 && (
                                                <div className="mt-3 flex gap-2 flex-wrap">
                                                    {msg.images.map((img, imgIndex) => (
                                                        <div
                                                            key={imgIndex}
                                                            className="relative group cursor-pointer"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openFullscreenImage(img);
                                                            }}
                                                        >
                                                            <img
                                                                src={img}
                                                                alt={`Message attachment ${imgIndex + 1}`}
                                                                className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300 group-hover:border-blue-400 transition-all group-hover:scale-110 shadow-sm"
                                                            />
                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                                                                <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {ticket.closePhotos && ticket.closePhotos.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                    Closing Photos ({ticket.closePhotos.length})
                                </h4>

                                <div className="grid grid-cols-2 gap-4">
                                    {ticket.closePhotos.map((photo, index) => (
                                        <div
                                            key={index}
                                            className="relative rounded-xl overflow-hidden bg-gray-50 border border-green-200 hover:border-green-400 transition-all cursor-pointer group"
                                            onClick={() => openFullscreenImage(photo)}
                                        >
                                            <img
                                                src={photo}
                                                alt={`Closing evidence ${index + 1}`}
                                                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                                            />

                                            <div className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded shadow">
                                                CLOSED
                                            </div>

                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                                <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                    </svg>
                                                </div>
                                            </div>

                                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                                {index + 1} / {ticket.closePhotos.length}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons - Only show if not using dropdown to change status */}
                        <div className="flex gap-3 pt-4 border-t border-gray-200">
                            {ticket.status === "resolved" && (
                                <div className="flex-1 px-4 py-2.5 bg-green-100 text-green-800 text-sm font-semibold rounded-lg text-center">
                                    <IoCheckmarkCircle className="w-5 h-5 inline mr-2" />
                                    Ticket Resolved
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Fullscreen Image Viewer */}
            {fullscreenImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-95 z-[60] flex items-center justify-center p-4"
                    onClick={closeFullscreenImage}
                >
                    <button
                        onClick={closeFullscreenImage}
                        className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors z-10 shadow-lg"
                    >
                        <IoClose className="w-7 h-7 text-gray-800" />
                    </button>

                    <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
                        <img
                            src={fullscreenImage}
                            alt="Full size preview"
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm">
                        Click anywhere to close
                    </div>
                </div>
            )}
        </>
    );
};

export default TicketModal;